import { FormattedMessage, useIntl } from "react-intl";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Gauss-Verteilungskurve ueber Umsatz und Kaufpreis, einmal PRO Vergleichsfilm
// gerechnet (siehe valuation.ts valuateByComparable) statt nur den Schnitt zu
// zeigen - Spec-Vorgabe "Film-Einschaetzungs-Agent" Schritt 26: "Gauss'sche
// Verteilungskurve ... Hervorhebung eines besonders wahrscheinlichen
// Erlösszenarios, aus dem dann die Empfehlung fuer einen maximalen Kaufpreis
// resultiert."
//
// BEWUSST NUR ZWEI Kurven, nicht drei: Umsatz und Kaufpreis streuen echt
// (jeder Vergleichsfilm bringt seine eigene DE-Verleihmiete mit), Aufwaende
// dagegen sind bei jedem Eintrag identisch - P&A/interne Aufwaende sind
// manuelle Eingaben fuer DIESES Projekt, keine Vergleichsfilm-Quelle (siehe
// TobisValuationManualInput). Eine "Kurve" ueber eine konstante Zahl waere nur
// ein Strich, keine Verteilung - stattdessen ein reiner Textwert darunter,
// siehe costCents unten. Das ist eine ehrliche Grenze der Datenlage, keine
// halbe Umsetzung.
//
// Gleiche visuelle Sprache wie TobisMarketCharts.tsx (GRID/AXIS_TEXT-Toene,
// ResponsiveContainer, monochrom+Strichmuster statt Farbe fuer mehrere Serien
// in einer Box) - hier bewusst NICHT von dort importiert: dieses Modul ist
// explizit auf das Markt-Dashboard verengt (siehe dessen Kommentar), dieser
// Chart gehoert zum Labor. Die zwei Farbwerte sind zu klein, um das Reuse
// vs. neue-Datei-Abwaegung zugunsten eines Imports ueber Modulgrenzen zu
// kippen.
const INK = "rgba(0,0,0,0.85)";
const GRID = "rgba(0,0,0,0.08)";
const AXIS_TEXT = "rgba(0,0,0,0.45)";
const CURVE_POINTS = 61;
const CURVE_RADIUS_STD_DEV = 3;

function meanAndStdDev(values: readonly number[]): { mean: number; stdDev: number } {
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  // Floor statt 0: eine Gruppe identischer Werte (z.B. genau ein Vergleichsfilm mit
  // Zahl, oder mehrere mit exakt derselben Besucherzahl) darf keinen Null-Nenner
  // erzeugen - eine sehr enge, fast senkrechte Kurve ist die ehrliche Darstellung
  // "kaum Streuung", nicht ein NaN.
  return { mean, stdDev: Math.max(Math.sqrt(variance), 1) };
}

// Auf den Scheitelpunkt normiert (Wert 1 bei x = mean) statt der echten
// Wahrscheinlichkeitsdichte (winzige Betraege, die als Y-Achse niemandem etwas
// sagen wuerden) - der 1/(stdDev*sqrt(2*pi))-Vorfaktor kuerzt sich dabei weg,
// es bleibt die reine Exponentialform. Zeigt die Form/Streuung ehrlich, ohne
// eine Dichte-Einheit zu behaupten, die im UI ohnehin niemand braucht.
function normalizedGaussian(x: number, mean: number, stdDev: number): number {
  const z = (x - mean) / stdDev;
  return Math.exp(-0.5 * z * z);
}

export interface ValuationDistributionComparable {
  id: string;
  name?: string;
  revenueTotal: number;
  maxPurchasePrice: number;
}

interface CurvePoint {
  x: number;
  revenue: number;
  price: number;
}

function buildCurve(
  revenues: readonly number[],
  prices: readonly number[],
): { points: CurvePoint[]; priceMean: number } {
  const rev = meanAndStdDev(revenues);
  const price = meanAndStdDev(prices);
  const xMin = Math.min(rev.mean - CURVE_RADIUS_STD_DEV * rev.stdDev, price.mean - CURVE_RADIUS_STD_DEV * price.stdDev);
  const xMax = Math.max(rev.mean + CURVE_RADIUS_STD_DEV * rev.stdDev, price.mean + CURVE_RADIUS_STD_DEV * price.stdDev);
  const step = (xMax - xMin) / (CURVE_POINTS - 1);
  const points: CurvePoint[] = Array.from({ length: CURVE_POINTS }, (_, i) => {
    const x = xMin + i * step;
    return {
      x,
      revenue: normalizedGaussian(x, rev.mean, rev.stdDev),
      price: normalizedGaussian(x, price.mean, price.stdDev),
    };
  });
  return { points, priceMean: price.mean };
}

export function TobisValuationDistribution({
  perComparable,
  costCents,
  money,
}: {
  perComparable: readonly ValuationDistributionComparable[];
  costCents: number;
  money: (cents: number) => string;
}) {
  const intl = useIntl();

  // Unter zwei Vergleichsfilmen ist eine Streuung statistisch nicht aussagekraeftig
  // (eine "Verteilung" aus einem einzigen Punkt ist keine) - ein Hinweis statt einer
  // erfundenen Kurve.
  if (perComparable.length < 2) {
    return (
      <p className="mt-6 text-[11px] text-ink/40">
        <FormattedMessage
          id="tobis.lab.valuation.distributionTooFew"
          defaultMessage="Fewer than two comparables with DE admissions - not enough for a distribution."
        />
      </p>
    );
  }

  const { points, priceMean } = buildCurve(
    perComparable.map((c) => c.revenueTotal),
    perComparable.map((c) => c.maxPurchasePrice),
  );

  return (
    <div className="mt-6">
      <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">
        <FormattedMessage
          id="tobis.lab.valuation.distributionHeading"
          defaultMessage="Distribution across comparables"
        />
      </div>
      {/* margin.top=28 + YAxis-Domain bis 1.35 statt 1.05: die "Wahrscheinlichstes
          Szenario"-Beschriftung der ReferenceLine (label position="top") braucht Platz
          UEBER dem Kurvenscheitel - bei zu wenig Kopfraum ragte sie aus der SVG-viewBox
          heraus und wurde vom umgebenden Layout abgeschnitten (Marvin, Screenshot 2026-08-08). */}
      <ResponsiveContainer width="100%" height={190}>
        <LineChart data={points} margin={{ top: 28, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
          <XAxis
            dataKey="x"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(v: number) => money(v)}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={{ stroke: GRID }}
            tickLine={false}
          />
          <YAxis hide domain={[0, 1.35]} />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <div className="box-flat bg-surface px-3 py-2 text-[12px] shadow-sm">
                  <div className="text-ink/55">{money(Number(label))}</div>
                  {payload.map((p) => (
                    <div key={p.dataKey as string} className="mt-0.5 tabular-nums text-ink">
                      {p.dataKey === "revenue"
                        ? intl.formatMessage({ id: "tobis.lab.valuation.distributionRevenue", defaultMessage: "Revenue" })
                        : intl.formatMessage({ id: "tobis.lab.valuation.distributionPrice", defaultMessage: "Purchase price" })}
                      : {intl.formatNumber(Number(p.value), { style: "percent", maximumFractionDigits: 0 })}
                    </div>
                  ))}
                </div>
              ) : null
            }
          />
          <ReferenceLine
            x={priceMean}
            stroke={INK}
            strokeDasharray="2 2"
            label={{
              value: intl.formatMessage({
                id: "tobis.lab.valuation.distributionMostLikely",
                defaultMessage: "Most likely",
              }),
              position: "top",
              fontSize: 10,
              fill: AXIS_TEXT,
            }}
          />
          <Line type="monotone" dataKey="revenue" stroke={INK} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line
            type="monotone"
            dataKey="price"
            stroke={INK}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink/55">
        <LegendSwatch dash={undefined} label={intl.formatMessage({ id: "tobis.lab.valuation.distributionRevenue", defaultMessage: "Revenue" })} />
        <LegendSwatch dash="6 4" label={intl.formatMessage({ id: "tobis.lab.valuation.distributionPrice", defaultMessage: "Purchase price" })} />
      </div>
      <p className="mt-2 text-[11px] text-ink/40">
        <FormattedMessage
          id="tobis.lab.valuation.distributionCostNote"
          defaultMessage="Cost stays fixed at {amount} across every comparable - it's a manual entry for this project, not sourced per title, so it has no curve of its own."
          values={{ amount: money(costCents) }}
        />
      </p>
      <p className="mt-1 text-[11px] text-ink/40">
        <FormattedMessage
          id="tobis.lab.valuation.distributionMeanHint"
          defaultMessage="{count} comparables with DE admissions - the peak marks the average, not a guaranteed outcome."
          values={{ count: perComparable.length }}
        />
      </p>
    </div>
  );
}

function LegendSwatch({ dash, label }: { dash: string | undefined; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <svg width="16" height="8" aria-hidden="true">
        <line x1="0" y1="4" x2="16" y2="4" stroke={INK} strokeWidth={2} strokeDasharray={dash} />
      </svg>
      {label}
    </div>
  );
}
